import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  CloudUpload as UploadingIcon,
  Description as DescriptionIcon,
  FolderOpen as EmptyIcon,
  DeleteOutline as DeleteIcon,
  Insights as InsightsIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase';
import { collections } from '../../../utils/firestore';
import { deleteDocument } from '../../../services/documentService';

/** Map document status to icon + color + label */
const STATUS_CONFIG = {
  uploading: {
    icon: <CircularProgress size={16} />,
    color: 'info',
    label: 'Uploading',
  },
  uploaded: {
    icon: <CheckCircleIcon fontSize="small" color="success" />,
    color: 'success',
    label: 'Uploaded',
  },
  processing: {
    icon: <HourglassIcon fontSize="small" color="warning" />,
    color: 'warning',
    label: 'Processing',
  },
  analyzed: {
    icon: <CheckCircleIcon fontSize="small" color="success" />,
    color: 'success',
    label: 'Analyzed',
  },
  completed: {
    icon: <CheckCircleIcon fontSize="small" color="success" />,
    color: 'success',
    label: 'Completed',
  },
  error: {
    icon: <ErrorIcon fontSize="small" color="error" />,
    color: 'error',
    label: 'Error',
  },
};

/**
 * Get the file type label from file name or content type.
 * @param {Object} doc - Document object
 * @returns {string}
 */
function getFileType(doc) {
  if (doc.contentType) {
    if (doc.contentType.includes('pdf')) return 'PDF';
    if (doc.contentType.includes('wordprocessingml')) return 'DOCX';
    if (doc.contentType.includes('csv')) return 'CSV';
    if (doc.contentType.includes('plain')) return 'TXT';
  }
  if (doc.fileName) {
    const ext = doc.fileName.split('.').pop().toUpperCase();
    if (['PDF', 'DOCX', 'TXT', 'CSV'].includes(ext)) return ext;
  }
  return 'FILE';
}

/**
 * Format a Firestore timestamp to a readable date string.
 * @param {Object|Date|null} ts - Timestamp
 * @returns {string}
 */
function formatDate(ts) {
  if (!ts) return '—';
  let date;
  if (ts.toDate && typeof ts.toDate === 'function') {
    date = ts.toDate();
  } else if (ts.seconds) {
    date = new Date(ts.seconds * 1000);
  } else if (ts instanceof Date) {
    date = ts;
  } else {
    return '—';
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * DocumentList — Real-time table of uploaded documents with status tracking.
 *
 * @param {Object} props
 * @param {string} props.orgId - Organization ID to filter documents
 */
export default function DocumentList({ orgId }) {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null); // doc object or null
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const handleDeleteClick = (doc) => {
    setDeleteTarget(doc);
  };

  const handleDeleteCancel = () => {
    if (deleting) return; // ignore cancel during in-flight delete
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteDocument(deleteTarget.id);
      const summary = `Deleted ${result.deleted.length} item(s)` +
        (result.errors.length > 0 ? ` (${result.errors.length} error(s) — see console)` : '');
      if (result.errors.length > 0) {
        console.warn('deleteDocument partial errors:', result.errors);
      }
      setSnackbar({
        open: true,
        severity: result.errors.length > 0 ? 'warning' : 'success',
        message: summary,
      });
      setDeleteTarget(null);
    } catch (err) {
      console.error('deleteDocument failed:', err);
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.message || 'Failed to delete document',
      });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!orgId) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const colRef = collection(db, collections.documents);
    const q = query(
      colRef,
      where('organizationId', '==', orgId),
      orderBy('uploadedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDocuments(docs);
        setLoading(false);
      },
      (err) => {
        console.error('DocumentList subscription error:', err);
        setError(err.message || 'Failed to load documents');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orgId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <ErrorIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (documents.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <EmptyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="subtitle1" color="text.secondary">
          No documents uploaded yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload a document above to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>File Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Uploaded</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Outputs</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((doc) => {
            const statusCfg = STATUS_CONFIG[doc.status] || {
              icon: <ErrorIcon fontSize="small" color="warning" />,
              color: 'warning',
              label: doc.status ? `Unknown: ${doc.status}` : 'No status',
            };
            const fileType = getFileType(doc);

            return (
              <TableRow key={doc.id} hover>
                {/* File Name */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                      {doc.title || doc.fileName || 'Untitled'}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Type */}
                <TableCell>
                  <Chip
                    label={fileType}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                  />
                </TableCell>

                {/* Uploaded */}
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(doc.uploadedAt)}
                  </Typography>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {statusCfg.icon}
                    <Chip
                      label={statusCfg.label}
                      size="small"
                      color={statusCfg.color}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>
                  {doc.status === 'error' && doc.error && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.25 }}>
                      {doc.error}
                    </Typography>
                  )}
                </TableCell>

                {/* Outputs / Links */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {/* Show "View Analysis" link as soon as the document is uploaded — */}
                    {/* the case study record exists from the moment WF1 fires.        */}
                    {(doc.status === 'uploaded' ||
                      doc.status === 'processing' ||
                      doc.status === 'analyzing' ||
                      doc.status === 'extracting' ||
                      doc.status === 'analyzed' ||
                      doc.status === 'completed') && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          doc.status === 'completed' || doc.status === 'analyzed' ? (
                            <InsightsIcon fontSize="small" />
                          ) : (
                            <AutoAwesomeIcon fontSize="small" />
                          )
                        }
                        onClick={() =>
                          navigate(`/drew/case-studies/cs_${doc.id}`)
                        }
                        sx={{ textTransform: 'none' }}
                      >
                        {doc.status === 'completed' || doc.status === 'analyzed'
                          ? 'View Analysis'
                          : 'Watch Live'}
                      </Button>
                    )}
                    {(doc.status === 'completed' || doc.status === 'uploaded') &&
                      doc.downloadURL && (
                        <Link
                          href={doc.downloadURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          PDF
                        </Link>
                      )}
                    {!['uploaded', 'processing', 'analyzing', 'extracting', 'analyzed', 'completed'].includes(
                      doc.status
                    ) && (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Actions */}
                <TableCell align="right">
                  <Tooltip title="Delete document and all derived analysis">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(doc)}
                      aria-label={`Delete ${doc.title || doc.fileName}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>

    {/* Delete confirmation dialog */}
    <Dialog
      open={Boolean(deleteTarget)}
      onClose={handleDeleteCancel}
      aria-labelledby="delete-doc-title"
    >
      <DialogTitle id="delete-doc-title">Delete document?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will permanently delete <strong>{deleteTarget?.title || deleteTarget?.fileName}</strong> along
          with the original file and any derived case study, risk alert, pattern library entry,
          guidance content, or OE adjustment generated from it. This cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDeleteCancel} disabled={deleting}>Cancel</Button>
        <Button
          onClick={handleDeleteConfirm}
          color="error"
          variant="contained"
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Snackbar for result */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        severity={snackbar.severity}
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
}
